import React from "react";
import { View, StyleSheet } from "react-native";
import { Text } from '@/components/common/text';
import {
  CONFIDENCE_DISPLAY_THRESHOLD,
  SENTIMENT_LABEL_GLYPH,
} from "@/constants/sentimentConstants";
import { useAdminColors } from "@/hooks/admin/useAdminColors";
import { onColor } from "@/constants/shared/theme/onColor";
import type { SentimentLabel, SentimentStatus } from "@/types/sentiment";

interface Props {
  status: SentimentStatus;
  label?: SentimentLabel;
  confidence?: number;
  errorReason?: string | null;
}

/**
 * Sentiment chip. Fills come from theme-aware status tokens so the chip tracks
 * the light/dark card behind it, and the label ink is derived from the fill via
 * onColor() — the previous fixed palette (e.g. neutral #9E9E9E) fell to ~2.7:1
 * on a white card and the darker tones failed on the dark admin card.
 */
export const SentimentBadge: React.FC<Props> = ({ status, label, confidence, errorReason }) => {
  const { semantic } = useAdminColors();

  if (status === "pending") {
    return <Chip fill={semantic.status.info} text="🔵 Analyzing" />;
  }
  if (status === "failed") {
    return (
      <Chip
        fill={semantic.status.warning}
        text="⚠️ Failed"
        accessibilityLabel={errorReason ?? "failed"}
      />
    );
  }
  if (!label || confidence === undefined || confidence < CONFIDENCE_DISPLAY_THRESHOLD) {
    return null;
  }

  const fill =
    label === "positive"
      ? semantic.status.success
      : label === "negative"
        ? semantic.status.error
        : semantic.border.secondary; // neutral
  return <Chip fill={fill} text={`${SENTIMENT_LABEL_GLYPH[label]} ${label}`} />;
};

const Chip: React.FC<{ fill: string; text: string; accessibilityLabel?: string }> = ({
  fill,
  text,
  accessibilityLabel,
}) => (
  <View style={[styles.chip, { backgroundColor: fill }]} accessibilityLabel={accessibilityLabel}>
    <Text style={[styles.text, { color: onColor(fill) }]}>{text}</Text>
  </View>
);

const styles = StyleSheet.create({
  chip: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10, alignSelf: "flex-start" },
  text: { fontSize: 12, fontWeight: "600" },
});
